// Derives a child account number from its parent. AccountNumber is free text server-side (only unique per
// company), so there is no convention to read from code — the existing chart IS the convention, and this
// learns it rather than hardcoding one.
import { FinancialAccount } from '../models/financial-account/financial-account';

/** The digit span a given level occupies, e.g. { start: 4, end: 5 } for the 5th digit. */
interface Segment {
  start: number;
  end: number;
}

/**
 * Learns which digits each level varies in, by comparing every account in the chart against its parent.
 *
 * <p>The scheme is documented on the entity as one digit per level, but the real data does not follow that
 * uniformly (level 2 sits at index 1 while some level-3 accounts vary at index 5). Reading it off the data
 * is therefore more reliable than encoding the documented map — and it stays correct if the chart is
 * renumbered.</p>
 */
function learnSegmentsByLevel(accounts: FinancialAccount[]): Map<number, Segment> {
  const byId = new Map(accounts.filter((a) => a.id).map((a) => [a.id!, a]));
  // Level -> span -> how many accounts agree on it. The most-agreed span wins, so a single hand-typed
  // oddity does not redefine the level for everyone.
  const votes = new Map<number, Map<string, number>>();

  for (const account of accounts) {
    const parent = account.parentAccountId ? byId.get(account.parentAccountId) : undefined;
    if (!parent || account.accountNumber?.length !== parent.accountNumber?.length) continue;

    let start = account.accountNumber.length;
    let end = -1;
    for (let i = 0; i < account.accountNumber.length; i++) {
      if (account.accountNumber[i] === parent.accountNumber[i]) continue;
      if (i < start) start = i;
      if (i > end) end = i;
    }
    if (end < start) continue;

    const level = account.level ?? (parent.level ?? 1) + 1;
    const key = `${start}:${end + 1}`;
    const perLevel = votes.get(level) ?? new Map<string, number>();
    perLevel.set(key, (perLevel.get(key) ?? 0) + 1);
    votes.set(level, perLevel);
  }

  const result = new Map<number, Segment>();
  for (const [level, perLevel] of votes) {
    const [key] = [...perLevel.entries()].sort((a, b) => b[1] - a[1])[0];
    const [start, end] = key.split(':').map(Number);
    result.set(level, { start, end });
  }
  return result;
}

/** Fallback for a level nothing in the chart has reached yet: the first digit the parent leaves at zero. */
function firstFreeDigit(parentNumber: string): Segment | null {
  const start = parentNumber.search(/0+$/);
  return start === -1 ? null : { start, end: start + 1 };
}

/**
 * Next available child number under `parent` — always sequential (1, 2, 3 …) within the digits that level
 * uses, and never a number already taken elsewhere in the company.
 *
 * <p>Returns null only when the parent's number leaves no room for another level.</p>
 */
export function nextChildAccountNumber(parent: FinancialAccount, accounts: FinancialAccount[]): string | null {
  const parentNumber = parent.accountNumber ?? '';
  if (!parentNumber) return null;

  const childLevel = (parent.level ?? 1) + 1;
  const segment = learnSegmentsByLevel(accounts).get(childLevel) ?? firstFreeDigit(parentNumber);
  if (!segment) return null;

  const { start, end } = segment;
  // The parent must actually leave these digits free; otherwise the span belongs to a shallower level.
  if (start >= parentNumber.length || parentNumber.slice(start, end).replace(/0/g, '') !== '') return null;

  const siblings = accounts
    .filter((a) => a.parentAccountId === parent.id && a.accountNumber?.length === parentNumber.length)
    .map((a) => a.accountNumber);
  const taken = new Set(accounts.map((a) => a.accountNumber));

  const highest = siblings.reduce((max, sibling) => Math.max(max, Number(sibling.slice(start, end)) || 0), 0);
  const build = (value: number): string | null => {
    let width = end - start;
    let from = start;
    // Widen leftward on overflow (09 -> 10), but only over digits the parent leaves at zero.
    while (String(value).length > width && from > 0 && parentNumber[from - 1] === '0') {
      from -= 1;
      width += 1;
    }
    if (String(value).length > width) return null;
    return parentNumber.slice(0, from) + String(value).padStart(width, '0') + parentNumber.slice(end);
  };

  // Sequential, but skipping anything already used — gaps and hand-typed numbers exist, and the server
  // rejects duplicates outright now that the user cannot correct the number by hand.
  for (let value = highest + 1; value < 10 ** (parentNumber.length - start); value++) {
    const candidate = build(value);
    if (candidate === null) return null;
    if (!taken.has(candidate)) return candidate;
  }
  return null;
}
